<template>
  <div class="smart-search-panel">
    <!-- Header unificado: título + tabs + colapsar filtros + cerrar panel -->
    <div class="smart-search-header">
      <v-icon small color="var(--kln-accent)" class="smart-search-header__icon">mdi-auto-fix</v-icon>
      <span class="smart-search-title">Búsqueda Inteligente</span>

      <div class="mode-tabs">
        <button
          type="button"
          class="mode-tab"
          :class="{ 'mode-tab--active': mode === 'text' }"
          @click="setMode('text')"
        >
          <v-icon small>mdi-text-search</v-icon>
          <span>Por texto</span>
        </button>
        <button
          v-if="canFaceSearch"
          type="button"
          class="mode-tab"
          :class="{ 'mode-tab--active': mode === 'face' }"
          @click="setMode('face')"
        >
          <v-icon small>mdi-face-recognition</v-icon>
          <span>Por rostro</span>
        </button>
      </div>

      <button
        type="button"
        class="filters-collapse"
        :aria-expanded="filtersOpen ? 'true' : 'false'"
        :title="filtersOpen ? 'Ocultar filtros' : 'Mostrar filtros'"
        @click="filtersOpen = !filtersOpen"
      >
        <v-icon small class="filters-collapse__chevron" :class="{ 'filters-collapse__chevron--open': filtersOpen }">
          mdi-chevron-down
        </v-icon>
      </button>
      <v-btn icon x-small v-auto-tooltip="'Cerrar'" @click="$emit('close')">
        <v-icon small>mdi-chevron-left</v-icon>
      </v-btn>
    </div>

    <!-- Body: filtros compactos arriba (colapsables), resultados abajo. -->
    <div class="smart-search-body">

      <v-expand-transition>
      <div v-show="filtersOpen" class="filters-row">
        <!-- ════════════════════ MODO TEXTO ════════════════════ -->
        <template v-if="mode === 'text'">
          <div class="filters-row__line">
            <v-text-field
              v-model="localSearchQuery"
              label="Texto de consulta"
              outlined
              dense
              hide-details
              class="filter-input filter-input--grow"
              @keyup.enter="updateSearch"
            ></v-text-field>
            <v-btn color="var(--kln-accent)" small class="update-button" :loading="loading" @click="updateSearch">
              <v-icon left small>mdi-refresh</v-icon>
              Buscar
            </v-btn>
          </div>

          <div class="filters-row__line">
            <v-autocomplete
              v-model="selectedCamera"
              :items="cameras"
              label="Cámaras"
              outlined
              dense
              hide-details
              class="filter-input filter-input--grow"
              clearable
              multiple
              small-chips
              item-text="text"
              item-value="value"
              return-object
              :menu-props="{ contentClass: 'kln-dense-menu', maxHeight: 260 }"
            ></v-autocomplete>

            <v-autocomplete
              v-model="selectedGroup"
              :items="groups"
              label="Grupos"
              outlined
              dense
              hide-details
              class="filter-input filter-input--grow"
              clearable
              multiple
              small-chips
              item-text="text"
              item-value="value"
              return-object
              :menu-props="{ contentClass: 'kln-dense-menu', maxHeight: 260 }"
            ></v-autocomplete>

            <v-btn outlined small color="var(--kln-accent)" class="radius-button filter-input" @click="openRadiusDialog">
              <v-icon left small>mdi-map-marker-radius</v-icon>
              {{ selectedRadius ? (selectedRadius.radiusMeters + ' m') : 'Radio' }}
            </v-btn>
          </div>

          <div class="filters-row__line">
            <v-text-field
              v-model="startDateTime"
              type="datetime-local"
              label="Desde"
              outlined
              dense
              hide-details
              class="filter-input filter-input--grow"
              prepend-inner-icon="mdi-calendar-clock"
            ></v-text-field>

            <v-text-field
              v-model="endDateTime"
              type="datetime-local"
              label="Hasta"
              outlined
              dense
              hide-details
              :min="startDateTime"
              class="filter-input filter-input--grow"
              prepend-inner-icon="mdi-calendar-clock"
            ></v-text-field>
          </div>
        </template>

        <!-- ════════════════════ MODO ROSTRO ════════════════════ -->
        <template v-else>
          <div class="face-grid">
            <!-- Columna izquierda: foto + botón buscar -->
            <div class="face-grid__col face-grid__col--photo">
              <div
                v-if="!photoFile"
                class="face-drop"
                @dragover.prevent
                @drop="onPhotoDrop"
                @click="$refs.fileInput.click()"
              >
                <v-icon size="32">mdi-camera-plus</v-icon>
                <span class="face-drop__text">
                  {{ $vuetify.lang.t('$vuetify.face_search.filters.photo') }}
                </span>
              </div>
              <div v-else class="face-drop face-drop--with-preview">
                <img :src="photoPreviewUrl" alt="" class="face-drop__thumb-big" />
                <v-btn x-small text color="var(--kln-text-secondary)" class="face-drop__remove" @click="clearPhoto">
                  <v-icon x-small left>mdi-close</v-icon>Quitar
                </v-btn>
              </div>
              <input
                ref="fileInput"
                type="file"
                accept="image/jpeg,image/png,image/webp"
                style="display:none"
                @change="onFileInputChange"
              />

              <v-btn
                color="var(--kln-accent)"
                block
                :loading="loading"
                :disabled="loading || !photoFile"
                @click="updateSearch"
              >
                <v-icon left small>mdi-magnify</v-icon>
                {{ $vuetify.lang.t('$vuetify.face_search.filters.search') }} rostros
              </v-btn>
            </div>

            <!-- Columna derecha: filtros apilados -->
            <div class="face-grid__col face-grid__col--filters">
              <!-- Modo de zona -->
              <div class="face-stack">
                <label class="filters-row__label">Modo de zona</label>
                <v-radio-group
                  :value="faceSearchFilters.mode"
                  row hide-details dense
                  class="face-zone-radios"
                  @change="onFaceZoneModeChange"
                >
                  <v-radio label="Todas" value="all" />
                  <v-radio label="Ciudad" value="city" />
                  <v-radio label="Radio" value="radius" />
                </v-radio-group>
              </div>

              <!-- Condicional: ciudad -->
              <div v-if="faceSearchFilters.mode === 'city'">
                <v-autocomplete
                  :value="faceSearchFilters.selectedCity"
                  :items="cities"
                  label="Ciudad"
                  outlined dense hide-details
                  clearable
                  no-data-text="No hay ciudades disponibles"
                  @change="updateFaceFilter('selectedCity', $event)"
                />
              </div>

              <!-- Condicional: radio -->
              <div v-if="faceSearchFilters.mode === 'radius'">
                <v-btn outlined small color="var(--kln-accent)" block @click="openRadiusDialog">
                  <v-icon left small>mdi-map-marker-radius</v-icon>
                  {{ selectedRadius ? (selectedRadius.radiusMeters + ' m') : 'Definir centro y radio' }}
                </v-btn>
              </div>

              <!-- Desde -->
              <v-text-field
                v-model="startDateTime"
                type="datetime-local"
                label="Desde"
                outlined dense hide-details
                class="face-datetime"
              />

              <!-- Hasta -->
              <v-text-field
                v-model="endDateTime"
                type="datetime-local"
                label="Hasta"
                outlined dense hide-details
                :min="startDateTime"
                class="face-datetime"
              />

              <!-- Similitud mínima -->
              <div class="face-stack">
                <label class="filters-row__label">
                  {{ $vuetify.lang.t('$vuetify.face_search.filters.min_similarity') }}: {{ faceSearchFilters.minSimilarity }}%
                </label>
                <v-slider
                  :value="faceSearchFilters.minSimilarity"
                  :min="50"
                  :max="100"
                  hide-details dense
                  color="var(--kln-accent)"
                  @input="updateFaceFilter('minSimilarity', $event)"
                />
              </div>
            </div>
          </div>
        </template>
      </div>
      </v-expand-transition>

      <v-divider class="smart-search-divider" />

      <div
        class="results-split"
        :class="{ 'results-split--two': showEnrichPane }"
      >
        <!-- Pane izquierdo: enrich (solo en modo rostro) -->
        <div v-if="showEnrichPane" class="results-split__pane results-split__pane--left">
          <div class="results-toolbar">
            <span class="results-toolbar__title">Datos del rostro</span>
          </div>
          <div class="results-pane-area">
            <FaceEnrichSection
              :state="enrichmentState"
              :enrichment="enrichment"
              @retry-enrich="_runFaceEnrich(photoFile)"
            />
          </div>
        </div>

        <!-- Pane derecho: cámaras / resultados (siempre) -->
        <div class="results-split__pane results-split__pane--right">
          <div class="results-toolbar">
            <span class="results-toolbar__title">
              {{ mode === 'face' ? 'Cámaras' : 'Resultados' }}
              <span v-if="searchResults.total" class="results-toolbar__count">({{ searchResults.total }})</span>
            </span>
            <div class="results-toolbar__cols">
              <button
                v-for="n in [1, 2, 4]"
                :key="n"
                type="button"
                class="col-btn"
                :class="{ 'col-btn--active': gridColumns === n }"
                :data-tip="n + ' columna' + (n === 1 ? '' : 's')"
                data-tip-pos="bottom"
                @click="gridColumns = n"
              >{{ n }}</button>
            </div>
          </div>

          <div class="results-area" :class="'results-area--cols-' + gridColumns">
            <div v-if="mode === 'face' && faceErrorMessage" class="face-error-msg face-error-msg--pane">
              <v-icon small color="error" class="mr-1">mdi-alert-circle-outline</v-icon>
              <span>{{ faceErrorMessage }}</span>
            </div>
            <AISearchResultsGrid
              :items="searchResults.items"
              :loading="loading"
              :total="searchResults.total"
              :search-query="mode === 'face' ? '' : localSearchQuery"
              :placeholder-text="resultsPlaceholderText"
              :get-image-url="getImageUrl"
              camera-name-clickable
              @image-click="openFullScreenImage"
              @camera-name-click="onGoToCamera"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Sub-dialogs (se renderizan en el body; no intervienen con el panel) -->
    <RadiusPickerDialog
      :value="radiusDialogOpen"
      :get-center="getCenter"
      :get-zoom="getZoom"
      :get-map-mode="getMapMode"
      :initial-radius="selectedRadius ? selectedRadius.radiusMeters : 500"
      @input="radiusDialogOpen = $event"
      @confirm="onRadiusConfirm"
    />

    <FullScreenAIResultViewer
      :value="fullScreenImageDialog"
      :item="fullScreenImageItem"
      :get-image-url="getImageUrl"
      @input="onFullScreenAIResultViewerInput"
    />
  </div>
</template>

<script>
import moment from '@/util/dayjs-config';
import axios from 'axios'
import { mapGetters, mapMutations } from 'vuex'
import { ITEM_TYPES } from '@/components/map/mapToolBar.constants'
import AISearchResultsGrid from '@/components/map/AISearchResultsGrid.vue'
import RadiusPickerDialog from '@/components/map/RadiusPickerDialog.vue'
import FullScreenAIResultViewer from '@/components/map/FullScreenAIResultViewer.vue'
import faceSearchService from '@/components/map-v2/data/face-search-service'
import FaceEnrichSection from '@/components/map-v2/panels/FaceEnrichSection.vue'

export default {
  name: 'SmartSearchPanel',

  components: {
    AISearchResultsGrid,
    RadiusPickerDialog,
    FullScreenAIResultViewer,
    FaceEnrichSection,
  },

  props: {
    initialQuery: { type: String, default: '' },
    initialMode: { type: String, default: 'text' },
    findItems: { type: Function, default: null },
    getGroups: { type: Function, default: null },
    getCenter: { type: Function, default: null },
    getZoom: { type: Function, default: null },
    getMapMode: { type: Function, default: null },
  },

  data() {
    return {
      mode: 'text',
      photoFile: null,
      photoPreviewUrl: '',
      enrichment: null,
      enrichmentState: 'idle',
      faceAbortController: null,
      faceEnrichAbortController: null,
      faceErrorMessage: '',
      faceSearched: false,
      localSearchQuery: '',
      selectedCamera: [],
      selectedGroup: [],
      selectedRadius: null,
      startDate: null,
      startTime: null,
      endDate: null,
      endTime: null,
      cameras: [],
      groups: [],
      cities: [],
      radiusDialogOpen: false,
      searchResults: { total: 0, items: [] },
      loading: false,
      fullScreenImageDialog: false,
      fullScreenImageItem: null,
      gridColumns: 2, // 1 | 2 | 4 — persiste la preferencia del operador.
      // Grupo de filtros colapsable. Por default abierto; al colapsar,
      // el grid de resultados gana el espacio vertical.
      filtersOpen: true,
    }
  },

  computed: {
    ...mapGetters('map-v2', ['faceSearchFilters', 'smartSearchInitialMode']),
    ...mapGetters('auth', ['getUser']),

    // La búsqueda por rostro identifica personas a partir de datos biométricos.
    // Hasta ahora el panel no pedía nada: bastaba con tener acceso al mapa, y
    // /map no lleva gate, así que la podían usar los 601 operadores. Las vistas
    // standalone que este panel sustituyó SÍ exigían `permission_face_search`;
    // al retirarlas, este panel quedó como única puerta y heredó el hueco.
    //
    // Mismo idiom que `canSeeUserStats` en MapToolbar. Se aceptan booleano y
    // cadena porque conviven valores de ambas formas en el padrón.
    canFaceSearch() {
      const user = this.getUser
      if (!user) return false
      if (user.administrator === true) return true
      const v = user.attributes && user.attributes.permission_face_search
      return v === true || v === 'true'
    },

    showEnrichPane() {
      // Siempre visible en modo rostro: muestra placeholder en estado idle
      // y rellena con los datos cuando se cargue la foto.
      return this.mode === 'face'
    },
    resultsPlaceholderText() {
      if (this.mode === 'face') {
        // Si hay error de la búsqueda, el banner rojo lo dice todo — sin placeholder
        if (this.faceErrorMessage) return ''
        // Ya se buscó y no hubo error: 0 resultados
        if (this.faceSearched) return 'No se encontraron coincidencias en las cámaras.'
        // Pre-búsqueda
        return this.photoFile
          ? 'Haz clic en "Buscar rostros" para detectar coincidencias.'
          : 'Carga una foto y haz clic en "Buscar rostros" para ver coincidencias en las cámaras.'
      }
      return 'Ingresa un texto de consulta y haz clic en "Buscar" para ver resultados'
    },
    // Bridge entre startDate + startTime (mantenidos separados por compat
    // con el action en el API) y el formato datetime-local del input
    // nativo (YYYY-MM-DDTHH:mm). Mismo patrón para endDateTime.
    startDateTime: {
      get() {
        return this.startDate && this.startTime ? this.startDate + 'T' + this.startTime : ''
      },
      set(val) {
        if (val) {
          const parts = val.split('T')
          this.startDate = parts[0]
          this.startTime = (parts[1] || '').slice(0, 5)
        } else {
          this.startDate = null
          this.startTime = null
        }
      },
    },
    endDateTime: {
      get() {
        return this.endDate && this.endTime ? this.endDate + 'T' + this.endTime : ''
      },
      set(val) {
        if (val) {
          const parts = val.split('T')
          this.endDate = parts[0]
          this.endTime = (parts[1] || '').slice(0, 5)
        } else {
          this.endDate = null
          this.endTime = null
        }
      },
    },

    // Resumen compacto de los filtros activos — se muestra a la derecha del
    // header cuando el grupo está colapsado, para dar visibilidad de los
    // criterios actuales sin tener que re-expandirlo.
    filterSummary() {
      const bits = []
      if (this.localSearchQuery) bits.push('"' + this.localSearchQuery + '"')
      if (this.selectedCamera && this.selectedCamera.length) bits.push(this.selectedCamera.length + ' cám.')
      if (this.selectedGroup && this.selectedGroup.length) bits.push(this.selectedGroup.length + ' grupo' + (this.selectedGroup.length > 1 ? 's' : ''))
      if (this.selectedRadius) bits.push(this.selectedRadius.radiusMeters + ' m')
      return bits.join(' · ')
    },
  },

  mounted() {
    const modoPedido = this.initialMode || this.smartSearchInitialMode || 'text'
    this.mode = (modoPedido === 'face' && !this.canFaceSearch) ? 'text' : modoPedido
    this.localSearchQuery = this.initialQuery || ''
    this.setDefaultDates()
    this.loadCameras()
    this.loadGroups()
    // Pre-carga ciudades si el modo de zona persistido es 'city' (para que
    // el autocomplete tenga opciones al primer render).
    if (this.faceSearchFilters && this.faceSearchFilters.mode === 'city') this.loadCities()
    // Sólo hacemos fetch inicial si hay query; abrir el panel vacío no dispara búsqueda.
    if (this.localSearchQuery) this.searchTextKalan()
  },

  watch: {
    initialQuery(newVal) {
      // Re-abre el panel (o cambia la query desde el lanzador): aplica la
      // nueva query y refetch. No reseteamos filtros para preservar cámaras
      // y rango que el operador haya seleccionado antes.
      if (newVal && newVal !== this.localSearchQuery) {
        this.localSearchQuery = newVal
        this.searchTextKalan()
      }
    },
  },

  methods: {
    ...mapMutations('map-v2', ['SET_FACE_SEARCH_FILTERS']),

    loadGroups() {
      if (!this.getGroups) { this.groups = []; return }
      const arr = this.getGroups()
      if (!Array.isArray(arr)) { this.groups = []; return }
      this.groups = arr
        .filter(g => g.branch === ITEM_TYPES.CAMERAS)
        .map(g => ({ text: g.name || g.id, value: g.id, ...g }))
    },

    loadCameras() {
      if (!this.findItems) { this.cameras = []; return }
      const allItems = this.findItems('')
      const camItems = allItems.filter(i => i.typeOfItem === ITEM_TYPES.CAMERAS)
      this.cameras = camItems.map(c => ({ text: c.name || c.id, value: c.id, ...c }))
    },

    loadCities() {
      if (!this.findItems) { this.cities = []; return }
      const allItems = this.findItems('')
      if (!Array.isArray(allItems)) { this.cities = []; return }
      const set = new Set()
      allItems
        .filter((it) => it.typeOfItem === ITEM_TYPES.CAMERAS)
        .forEach((cam) => {
          const c = cam.kalan_pmiCity || (cam.attributes && cam.attributes.kalan_pmiCity)
          if (c && typeof c === 'string') set.add(c)
        })
      this.cities = Array.from(set).sort((a, b) => a.localeCompare(b))
    },

    onFaceZoneModeChange(newMode) {
      this.updateFaceFilter('mode', newMode)
      // Limpiar selección previa al cambiar de modo (evita queries con city
      // y radius simultáneos).
      if (newMode !== 'city') this.updateFaceFilter('selectedCity', null)
      if (newMode !== 'radius') this.selectedRadius = null
      // Carga perezosa de ciudades al primer acceso al modo 'city'.
      if (newMode === 'city' && this.cities.length === 0) this.loadCities()
    },

    setDefaultDates() {
      // Ventana de 24h hacia atrás: las búsquedas (texto y rostro) son
      // típicamente históricas, así que arrancamos con un día completo
      // para que el operador encuentre resultados al primer intento.
      const now = moment()
      const s = moment(now).subtract(24, 'hours')
      this.startDate = s.format('YYYY-MM-DD')
      this.startTime = s.format('HH:mm')
      this.endDate = now.format('YYYY-MM-DD')
      this.endTime = now.format('HH:mm')
    },

    openRadiusDialog() { this.radiusDialogOpen = true },

    onRadiusConfirm(payload) { this.selectedRadius = payload },

    openFullScreenImage(item) {
      // El clic en la IMAGEN solo abre el visor en pantalla completa (es lo
      // principal). Ir al PMI se hace con el clic en el NOMBRE de la cámara
      // (onGoToCamera) — ya no se dispara el flyTo desde acá.
      // En modo rostro: la card muestra el recorte (face_image); al abrir
      // fullscreen swapeamos al frame completo y re-aplicamos el bbox.
      if (this.mode === 'face' && item && item.frameImageUrl) {
        this.fullScreenImageItem = {
          ...item,
          imageUrl: item.frameImageUrl,
          rectangle: item.frameRectangle,
        }
      } else {
        this.fullScreenImageItem = item
      }
      this.fullScreenImageDialog = true
    },

    onFullScreenAIResultViewerInput(open) {
      this.fullScreenImageDialog = open
      if (!open) this.fullScreenImageItem = null
    },

    // Clic en el NOMBRE de la cámara de un resultado → centrar el mapa en el
    // PMI y abrir su detalle en el drawer derecho. Solo emitimos los
    // identificadores del hit; MapV2 (onSmartSearchGoToCamera) resuelve el
    // monitor de cámara (catálogo por kalan_pmiId + ids candidatos) y hace
    // flyTo + selectItem, con aviso si la cámara no está en el mapa.
    onGoToCamera(item) {
      if (!item) return
      const raw = item._raw || item
      this.$emit('go-to-camera', {
        pmiId: raw.kalan_pmiId != null ? raw.kalan_pmiId : null,
        cameraId: raw.cameraId || raw.camera_id || raw.cameraID || null,
        pmiName: raw.kalan_pmiName || null,
        raw,
      })
    },

    getImageUrl(url) {
      if (!url) return ''
      const u = url.replace('/images/', '/api/intelli/images/')
      if (u.startsWith('http://') || u.startsWith('https://')) return u
      return u
    },

    async searchTextKalan() {
      try {
        this.loading = true
        const beginDate = moment(this.startDate + ' ' + this.startTime, 'YYYY-MM-DD HH:mm').toISOString()
        const endDateIso = moment(this.endDate + ' ' + this.endTime, 'YYYY-MM-DD HH:mm').toISOString()
        const pmiIds = Array.isArray(this.selectedCamera)
          ? this.selectedCamera.map(c => (typeof c === 'object' ? (c.id || c.value) : c))
          : []
        const groupIds = Array.isArray(this.selectedGroup)
          ? this.selectedGroup.map(g => (typeof g === 'object' ? (g.id || g.value) : g))
          : []
        const resp = await axios.post('/api/intelli/prompt/search-text-kalan', {
          text: this.localSearchQuery,
          pmiIds,
          groupIds,
          beginDate,
          endDate: endDateIso,
          searchZone: this.selectedRadius,
        })
        if (resp.data && resp.data.data) {
          this.searchResults = {
            total: resp.data.data.total || 0,
            items: resp.data.data.items || [],
          }
        } else {
          this.searchResults = { total: 0, items: [] }
        }
      } catch (err) {
        console.error('SmartSearch error:', err)
        this.searchResults = { total: 0, items: [] }
      } finally {
        this.loading = false
      }
    },

    updateSearch() {
      if (this.mode === 'face') this.searchFaces()
      else this.searchTextKalan()
    },

    async searchFaces() {
      // Defensa en profundidad: aquí es donde sale la petición al backend, así
      // que el permiso se comprueba en el punto de ejecución y no solo en la UI.
      if (!this.canFaceSearch) return
      if (!this.photoFile) {
        this.faceErrorMessage = this.$vuetify.lang.t('$vuetify.face_search.filters.no_photo_yet')
        return
      }
      this.faceErrorMessage = ''
      if (this.faceAbortController) this.faceAbortController.abort()
      this.faceAbortController = new AbortController()
      this.loading = true
      this.searchResults = { items: [], total: 0 }

      // Dates desde data local (consistente con searchTextKalan)
      const startDateMillis = moment(this.startDate + ' ' + this.startTime, 'YYYY-MM-DD HH:mm').valueOf()
      const endDateMillis = moment(this.endDate + ' ' + this.endTime, 'YYYY-MM-DD HH:mm').valueOf()
      const minSimilarity = (this.faceSearchFilters && this.faceSearchFilters.minSimilarity) || 80

      const zoneMode = (this.faceSearchFilters && this.faceSearchFilters.mode) || 'all'
      const params = {
        mode: zoneMode,
        start_date: Number.isNaN(startDateMillis) ? null : startDateMillis,
        end_date: Number.isNaN(endDateMillis) ? null : endDateMillis,
        min_detection_similarity: minSimilarity,
        limit: 50,
        offset: 0,
      }
      if (zoneMode === 'city' && this.faceSearchFilters && this.faceSearchFilters.selectedCity) {
        params.city = this.faceSearchFilters.selectedCity
      }
      if (zoneMode === 'radius' && this.selectedRadius && this.selectedRadius.center) {
        params.lat = this.selectedRadius.center[1]
        params.lng = this.selectedRadius.center[0]
        params.radius_m = this.selectedRadius.radiusMeters
      }

      // Enrich en paralelo
      this._runFaceEnrich(this.photoFile)

      try {
        const data = await faceSearchService.search({
          photo: this.photoFile,
          params,
          signal: this.faceAbortController.signal,
        })
        const detections = Array.isArray(data.detections) ? data.detections : []
        const cameraIndex = this._buildFaceCameraIndex(data.searched_cameras)
        const items = detections.map((d) => this._mapFaceDetection(d, cameraIndex))
        this.searchResults = {
          items,
          total: typeof data.total === 'number' ? data.total : items.length,
        }
      } catch (err) {
        const code = faceSearchService.classifyError(err)
        if (code === 'cancelled') return
        if (code === 'bad_request') {
          // Mensaje específico del backend (ej. "Las cámaras filtradas no tienen analytics...")
          this.faceErrorMessage = faceSearchService.extractBackendMessage(err)
            || this.$vuetify.lang.t('$vuetify.face_search.errors.unavailable')
        } else {
          this.faceErrorMessage = this._faceErrorMessage(code)
          if (code === 'session_expired' && window.VMA && window.VMA.$emit) {
            window.VMA.$emit('AUTH_FAIELD')
          }
        }
      } finally {
        this.loading = false
        this.faceSearched = true
      }
    },

    _buildFaceCameraIndex(cameras) {
      const byUuid = new Map()
      const byIdNum = new Map()
      const byAnalyticsId = new Map()
      if (!Array.isArray(cameras)) return { byUuid, byIdNum, byAnalyticsId }
      cameras.forEach((cam) => {
        if (!cam) return
        if (cam.incoresoft_id) byUuid.set(cam.incoresoft_id, cam)
        if (cam.incoresoft_id_num != null) byIdNum.set(cam.incoresoft_id_num, cam)
        if (Array.isArray(cam.analytics_ids)) {
          cam.analytics_ids.forEach((id) => byAnalyticsId.set(id, cam))
        }
      })
      return { byUuid, byIdNum, byAnalyticsId }
    },
    _mapFaceDetection(detection, index) {
      const analytics = detection.analytics || {}
      const camera = index.byUuid.get(analytics.stream_uuid)
        || index.byIdNum.get(analytics.stream_id)
        || index.byAnalyticsId.get(analytics.id)
        || null
      // bbox: legacy usa detection.box como [x1,y1,x2,y2]; convertir a {x,y,width,height}
      let rectangle = null
      if (Array.isArray(detection.box) && detection.box.length === 4) {
        const [x1, y1, x2, y2] = detection.box
        rectangle = {
          x: x1, y: y1, width: x2 - x1, height: y2 - y1,
        }
      } else if (detection.bbox || detection.rectangle) {
        rectangle = detection.bbox || detection.rectangle
      }
      // Campos de cámara: del _camera del index o del detection mismo (fallback)
      const cam = camera || {}
      const pmiName = cam.kalan_pmiName || detection.kalan_pmiName || detection.pmiName || ''
      const cameraDev = cam.incoresoft_name || detection.kalan_cameraDev || detection.cameraDev || ''
      const pmiCity = cam.kalan_pmiCity || detection.kalan_pmiCity || detection.kalan_pmi_city || detection.pmiCity || ''
      // Similarity: legacy probe confidence > similarity > detection_similarity
      let similarity = detection.confidence
      if (typeof similarity !== 'number') similarity = detection.similarity
      if (typeof similarity !== 'number') similarity = detection.detection_similarity
      // Card = recorte del rostro (face_image); fullscreen = frame completo
      // (frame_image). Paridad con legacy FaceSearchModal.getCardImageUrl /
      // getImageUrl. El bbox solo aplica al frame, así que la card no lo
      // muestra (queda como frameRectangle para el viewer).
      const rawFace = detection.face_image || detection.frame_image || ''
      const rawFrame = detection.frame_image || detection.face_image || ''
      const imageUrl = faceSearchService.resolveImageUrl(rawFace)
      const frameImageUrl = faceSearchService.resolveImageUrl(rawFrame)
      return {
        id: detection.id || detection._id,
        imageUrl,
        frameImageUrl,
        frameRectangle: rectangle,
        kalan_pmiName: pmiName,
        kalan_pmiCity: pmiCity,
        kalan_cameraDev: cameraDev,
        collectTime: detection.timestamp || detection.collectTime,
        similarity,
        _raw: detection,
        _camera: camera,
      }
    },
    async _runFaceEnrich(photo) {
      // El enriquecimiento también sube la foto al backend, y se puede
      // disparar desde el botón de reintento de FaceEnrichSection, no solo
      // desde searchFaces. Lleva su propia comprobación.
      if (!this.canFaceSearch) return
      this.enrichment = null
      this.enrichmentState = 'loading'
      const identifier = await faceSearchService.loadConsultaMurphy()
      if (!identifier) { this.enrichmentState = 'no_identifier'; return }
      if (!photo) { this.enrichmentState = 'no_photo'; return }
      if (this.faceEnrichAbortController) this.faceEnrichAbortController.abort()
      this.faceEnrichAbortController = new AbortController()
      try {
        const data = await faceSearchService.enrich({
          identifier,
          photo,
          signal: this.faceEnrichAbortController.signal,
        })
        this.enrichment = data
        this.enrichmentState = 'success'
      } catch (err) {
        const code = faceSearchService.classifyError(err)
        if (code === 'cancelled') return
        this.enrichmentState = code === 'session_expired' || code === 'file_too_large' ? code : 'enrich_failed'
      }
    },

    _faceErrorMessage(code) {
      const map = {
        session_expired: 'face_search.errors.session_expired',
        file_too_large: 'face_search.errors.file_too_large',
        no_face: 'face_search.errors.no_face',
        unavailable: 'face_search.errors.unavailable',
        network: 'face_search.errors.network',
      }
      return this.$vuetify.lang.t(`$vuetify.${map[code] || 'face_search.errors.unavailable'}`)
    },

    setMode(m) {
      // Ocultar la pestaña no es un gate: el modo se puede fijar por store
      // (`smartSearchInitialMode`) o desde el lanzador. Se comprueba aquí.
      if (m === 'face' && !this.canFaceSearch) return
      if (this.mode === m) return
      this.mode = m
      if (this.faceAbortController) this.faceAbortController.abort()
      if (this.faceEnrichAbortController) this.faceEnrichAbortController.abort()
      this.searchResults = { items: [], total: 0 }
      this.enrichment = null
      this.enrichmentState = 'idle'
      this.faceErrorMessage = ''
      this.faceSearched = false
    },

    updateFaceFilter(key, value) {
      const patch = {}
      patch[key] = value
      this.SET_FACE_SEARCH_FILTERS(patch)
    },

    onPhotoSelected(file) {
      if (!file) return
      const validTypes = ['image/jpeg', 'image/png', 'image/webp']
      if (!validTypes.includes(file.type)) {
        this.faceErrorMessage = this.$vuetify.lang.t('$vuetify.face_search.filters.photo_invalid_type')
        return
      }
      if (file.size > 10 * 1024 * 1024) {
        this.faceErrorMessage = this.$vuetify.lang.t('$vuetify.face_search.filters.photo_too_large')
        return
      }
      this.faceErrorMessage = ''
      if (this.photoPreviewUrl) URL.revokeObjectURL(this.photoPreviewUrl)
      this.photoFile = file
      this.photoPreviewUrl = URL.createObjectURL(file)
    },

    clearPhoto() {
      this.photoFile = null
      if (this.photoPreviewUrl) {
        URL.revokeObjectURL(this.photoPreviewUrl)
        this.photoPreviewUrl = ''
      }
      this.faceSearched = false
      this.faceErrorMessage = ''
      this.searchResults = { items: [], total: 0 }
    },

    onFileInputChange(e) {
      const file = e.target.files && e.target.files[0]
      this.onPhotoSelected(file)
      e.target.value = ''
    },

    onPhotoDrop(e) {
      e.preventDefault()
      const file = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0]
      this.onPhotoSelected(file)
    },
  },

  beforeDestroy() {
    if (this.photoPreviewUrl) URL.revokeObjectURL(this.photoPreviewUrl)
    if (this.faceAbortController) this.faceAbortController.abort()
    if (this.faceEnrichAbortController) this.faceEnrichAbortController.abort()
  },
}
</script>

<style scoped>
.smart-search-panel {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  min-height: 0;
  background: var(--kln-bg-surface, #0d1220);
  overflow: hidden;
}

/* Header unificado: título a la izq, tabs en el medio (alineados al borde
   inferior para el efecto tab-pegado), botones de colapsar y cerrar a la
   derecha. */
.smart-search-header {
  display: flex;
  align-items: flex-end;
  gap: 8px;
  padding: 8px 10px 0;
  background: var(--kln-bg-deep);
  border-bottom: 1px solid var(--kln-border-dim);
  flex-shrink: 0;
}

.smart-search-header__icon {
  margin-bottom: 6px;
}

.smart-search-title {
  font-size: 12px;
  font-weight: 600;
  color: var(--kln-text, #fff);
  letter-spacing: 0.02em;
  margin-right: auto;
  margin-bottom: 6px;
  white-space: nowrap;
}

/* Botones de la derecha del header (filtros + cerrar) alineados al centro
   vertical para no quedar pegados al fondo como los tabs. */
.smart-search-header .filters-collapse,
.smart-search-header .v-btn--icon {
  margin-bottom: 4px;
  align-self: center;
}

.smart-search-divider {
  border-color: var(--kln-border-dim) !important;
}

.smart-search-body {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
}

/* Fila con tabs Por texto/Por rostro (izquierda) y botón colapsar (derecha).
   Padding-bottom 0 para que los tabs lleguen hasta la línea inferior y el
   tab activo pueda "romperla" con su border-bottom del color del bg. */
.filters-head-row {
  display: flex;
  align-items: flex-end;
  gap: 12px;
  padding: 6px 10px 0;
  background: var(--kln-bg-deep, #0a0e1a);
  border-bottom: 1px solid var(--kln-border-dim);
  flex-shrink: 0;
}

.filters-collapse {
  margin-left: auto;
  margin-bottom: 4px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 4px;
  background: transparent;
  cursor: pointer;
  flex-shrink: 0;
  transition: background 0.15s;
  align-self: center;
}

.filters-collapse:hover {
  background: var(--kln-border-dim);
}

.filters-collapse__chevron {
  color: var(--kln-text-muted) !important;
  transition: transform 0.2s ease;
}

.filters-collapse__chevron--open {
  transform: rotate(180deg);
}

/* Header del grupo de filtros: click para colapsar/expandir */
.filters-head {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  background: transparent;
  border: none;
  cursor: pointer;
  font: inherit;
  text-align: left;
  flex: 1;
  flex-shrink: 0;
  transition: background 0.15s;
}

.filters-head:hover {
  background: var(--kln-border-dim);
}

.filters-head__chevron {
  color: var(--kln-text-muted) !important;
  transition: transform 0.2s ease;
}

.filters-head__chevron--open {
  transform: rotate(90deg);
}

.filters-head__label {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--kln-text-muted);
}

.filters-head__summary {
  margin-left: auto;
  font-size: 11px;
  color: var(--kln-text-dim);
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 55%;
}

.filters-row {
  display: flex;
  flex-direction: column;
  /* Gap generoso entre filas: las etiquetas flotantes (outlined v-text-field)
     suben ~8 px cuando el campo tiene valor; un gap pequeño las hace parecer
     encimadas con el borde inferior de la fila anterior. */
  gap: 14px;
  padding: 14px 10px 10px;
  background: var(--kln-bg-deep, #0a0e1a);
  flex-shrink: 0;
}

.filters-row__line {
  display: flex;
  gap: 6px;
  align-items: stretch;
}

.filter-input {
  flex: 0 0 auto;
}

.filter-input--grow {
  flex: 1 1 0;
  min-width: 0;
}

/* Input styling — altura unificada de 40px en todos los campos + botones
   para que la fila se vea uniforme. Los v-autocomplete multiples con
   small-chips fuerzan ~40px mínimo, así que alineamos el resto a ese
   baseline en vez de comprimirlos. Alineado con el "back office clean"
   del MapSidebar pero más respirable ahora que el grupo es colapsable. */
.filter-input >>> .v-input__slot {
  min-height: 40px !important;
  padding: 0 12px !important;
  background: var(--kln-border-dim) !important;
}

.filter-input >>> .v-text-field--outlined fieldset {
  border-color: var(--kln-border-normal) !important;
}

.filter-input >>> .v-text-field--outlined.v-input--is-focused fieldset {
  border-color: var(--kln-accent) !important;
  border-width: 1px !important;
}

.filter-input >>> input,
.filter-input >>> .v-select__selection {
  font-size: 12px !important;
  padding: 0 !important;
  min-height: 38px !important;
}

.filter-input >>> label,
.filter-input >>> .v-label {
  font-size: 12px !important;
  color: var(--kln-text-muted) !important;
  top: 12px !important;
}

.filter-input >>> .v-label--active {
  transform: translateY(-22px) scale(0.85) !important;
}

.filter-input >>> .v-input__prepend-inner {
  margin-top: 10px !important;
  padding-right: 6px !important;
}

.filter-input >>> .v-input__prepend-inner .v-icon {
  font-size: 16px !important;
}

.filter-input >>> .v-input__append-inner {
  margin-top: 9px !important;
  padding-left: 0 !important;
}

.filter-input >>> .v-input__append-inner .v-icon {
  font-size: 16px !important;
}

.radius-button,
.update-button {
  text-transform: none;
  letter-spacing: 0;
  font-size: 12px;
  font-weight: 500;
  /* Empata el alto unificado de los inputs (40px) para alinear la fila. */
  height: 40px !important;
  min-width: 0;
  padding: 0 12px !important;
}

.update-button {
  color: #000 !important;
  white-space: nowrap;
}

/* El input datetime-local nativo muestra un calendar/clock picker pequeño
   del browser al clickear. No hay CSS extra para el popup (lo renderiza
   el browser fuera del viewport del DOM). Solo aseguramos que el texto
   del input respete la paleta oscura. */
.filter-input >>> input[type="datetime-local"] {
  color: var(--kln-text) !important;
  /* El picker nativo de Chrome tinte el icono con color-scheme dark. */
  color-scheme: dark;
}

/* ── Results split: pane izq (enrich, solo modo rostro) + pane der (cámaras) ── */
.results-split {
  display: flex;
  flex: 1;
  min-height: 0;
  background: var(--kln-bg-surface);
}

.results-split__pane {
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
}

.results-split__pane--right {
  flex: 1;
}

.results-split--two .results-split__pane--left {
  flex: 1 1 0;
  border-right: 1px solid var(--kln-border-dim);
}

.results-split--two .results-split__pane--right {
  flex: 1 1 0;
}

.results-pane-area {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 0 12px 12px;
}

.results-pane-area::-webkit-scrollbar {
  width: 6px;
}

.results-pane-area::-webkit-scrollbar-thumb {
  background: var(--kln-border-normal);
  border-radius: 3px;
}

/* ── Results toolbar (título + selector de columnas del grid) ── */
.results-toolbar {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px 4px;
  background: var(--kln-bg-surface);
}

.results-toolbar__title {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--kln-text-muted);
  flex: 1;
}

.results-toolbar__count {
  color: var(--kln-accent);
  font-weight: 600;
  margin-left: 4px;
}

.results-toolbar__cols {
  display: flex;
  gap: 2px;
}

.col-btn {
  width: 26px;
  height: 22px;
  font: inherit;
  font-size: 11px;
  font-weight: 600;
  color: var(--kln-text-muted);
  background: var(--kln-border-dim);
  border: 1px solid var(--kln-border-dim);
  border-radius: 4px;
  cursor: pointer;
  transition: background 0.15s, color 0.15s, border-color 0.15s;
}

.col-btn:hover {
  background: var(--kln-border-dim);
  color: var(--kln-text);
}

.col-btn--active {
  background: var(--kln-accent-soft, rgba(0, 212, 255, 0.12));
  color: var(--kln-accent);
  border-color: var(--kln-accent);
}

.results-area {
  flex: 1;
  min-width: 0;
  min-height: 0;
  overflow-y: auto;
  background: var(--kln-bg-surface);
}

/* El AISearchResultsGrid legacy usa width:70% + padding 24px + background
   var(--map-card-background) hardcoded porque fue diseñado para vivir
   side-by-side con el panel de filtros dentro del modal. Aquí lo queremos
   full-width, sin padding extra, y con el fondo del panel — por eso
   overridear con ::v-deep. (Si se toca el componente hijo, Map.vue legacy
   rompe su layout 30/70.) */
.results-area ::v-deep .results-panel {
  width: 100% !important;
  padding: 0 12px 12px !important;
  background: transparent !important;
}

/* El título "Resultados (N)" del componente hijo se oculta porque lo
   renderizamos en .results-toolbar (junto al selector de columnas). */
.results-area ::v-deep .results-title {
  display: none !important;
}

/* Control de columnas del grid: override del default auto-fill del hijo. */
.results-area--cols-1 ::v-deep .results-grid {
  grid-template-columns: 1fr !important;
  gap: 12px !important;
}

.results-area--cols-2 ::v-deep .results-grid {
  grid-template-columns: repeat(2, 1fr) !important;
  gap: 10px !important;
}

.results-area--cols-4 ::v-deep .results-grid {
  grid-template-columns: repeat(4, 1fr) !important;
  gap: 8px !important;
}

/* Los result-items del grid compartido usan bg blanco 5% y border
   --map-card-border (no definida en MapV2). Le damos bg + border
   compatible con el tema v2 para integrar. */
.results-area ::v-deep .result-item {
  background: var(--kln-border-dim) !important;
  border: 1px solid var(--kln-border-dim) !important;
}

.results-area ::v-deep .image-container {
  background: var(--kln-bg-deep, #0a0e1a) !important;
}

.results-area ::v-deep .image-info {
  background: rgba(0, 0, 0, 0.25) !important;
}

.results-area::-webkit-scrollbar {
  width: 6px;
}

.results-area::-webkit-scrollbar-thumb {
  background: var(--kln-border-normal);
  border-radius: 3px;
}

/* ── Mode tabs: ahora viven dentro del header unificado junto al título
   y los botones; ocupan el espacio natural del contenido ── */
.mode-tabs {
  display: flex;
  gap: 4px;
  flex: 0 0 auto;
}

.mode-tab {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 8px 12px;
  border: 1px solid transparent;
  border-radius: 6px 6px 0 0;
  background: var(--kln-border-dim);
  color: var(--kln-text-muted);
  font-size: 12px;
  font-weight: 500;
  font-family: inherit;
  cursor: pointer;
  transition: background 0.2s, color 0.2s, border-color 0.2s;
  letter-spacing: 0.02em;
  white-space: nowrap;
  flex: 1 1 0;
  min-width: 0;
  position: relative;
  margin-bottom: -1px;
}

.mode-tab:hover {
  background: var(--kln-border-dim);
  color: var(--kln-text-muted);
}

.mode-tab--active {
  background: var(--kln-accent-soft);
  color: var(--kln-accent);
  border-color: var(--kln-accent);
  /* Border-bottom del color del bg del row tapa la línea horizontal del
     row exactamente en la zona del tab activo, dando el look "tab pegado
     al contenido". El margin-bottom: -1px del .mode-tab base lo alinea. */
  border-bottom-color: var(--kln-bg-deep, #0a0e1a);
  z-index: 1;
}

.mode-tab--active:hover {
  background: var(--kln-accent-soft);
  color: var(--kln-accent);
}

/* ── Modo rostro: grid de 2 columnas (foto izq, filtros der) ── */
.face-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  align-items: start;
}

.face-grid__col {
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: 0;
}

.face-stack {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

/* ── Foto drop zone (columna izq, ocupa lo disponible verticalmente) ── */
.face-drop {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 14px 10px;
  border: 1px dashed var(--kln-border-subtle);
  border-radius: 6px;
  cursor: pointer;
  background: var(--kln-border-dim);
  color: var(--kln-text-secondary);
  transition: border-color 0.15s, background 0.15s;
  flex: 1 1 auto;
  min-height: 160px;
}

.face-drop:hover {
  border-color: var(--kln-accent);
  background: var(--kln-border-dim);
}

.face-drop__text {
  font-size: 11px;
  text-align: center;
}

.face-drop--with-preview {
  cursor: default;
  padding: 8px;
  gap: 6px;
}

.face-drop__thumb-big {
  max-width: 100%;
  max-height: 140px;
  object-fit: contain;
  border-radius: 4px;
  border: 1px solid var(--kln-border-subtle);
  background: var(--kln-bg-deeper);
}

.face-drop__remove {
  margin-top: auto;
}

/* ── Labels y radios del modo rostro ── */
.filters-row__label {
  font-size: 11px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: var(--kln-text-muted);
}

.face-zone-radios {
  margin-top: 0 !important;
  padding-top: 0 !important;
}

.face-zone-radios ::v-deep .v-input__control {
  margin-top: 0 !important;
}

.face-zone-radios ::v-deep .v-input__slot {
  margin-bottom: 0 !important;
  padding: 0 !important;
}

.face-zone-radios ::v-deep .v-radio {
  margin-right: 10px !important;
  margin-bottom: 0 !important;
}

.face-zone-radios ::v-deep .v-radio:last-child {
  margin-right: 0 !important;
}

.face-zone-radios ::v-deep .v-label {
  font-size: 12px !important;
  color: var(--kln-text) !important;
}

.face-zone-radios ::v-deep .v-input--selection-controls {
  margin-top: 0 !important;
  padding-top: 0 !important;
}

/* Inputs datetime-local en la columna derecha del modo rostro.
   El input nativo del browser ya incluye un ícono de calendario al final;
   evitamos el prepend-inner-icon redundante y reducimos el font-size para
   que el texto + el ícono nativo entren en la columna estrecha. */
.face-grid__col--filters ::v-deep .v-text-field--outlined fieldset {
  border-color: var(--kln-border-dim) !important;
}

.face-datetime ::v-deep input[type="datetime-local"] {
  font-size: 12px !important;
  color: var(--kln-text) !important;
  color-scheme: dark;
  padding: 0 !important;
  min-width: 0;
}

.face-datetime ::v-deep .v-input__slot {
  padding: 0 10px !important;
}

/* ── Face error message ── */
.face-error-msg {
  font-size: 11px;
  color: #f44336;
  padding: 2px 0;
}

/* Variante para el mensaje de error dentro del pane de cámaras: ocupa todo
   el ancho de la zona de resultados con un fondo discreto. */
.face-error-msg--pane {
  display: flex;
  align-items: center;
  font-size: 12px;
  padding: 8px 12px;
  margin: 8px 12px;
  background: rgba(244, 67, 54, 0.08);
  border-left: 3px solid #f44336;
  border-radius: 4px;
  line-height: 1.4;
}
</style>
